import { Flexbox } from '@/ui/flexbox/flexbox';
import type { CompanySearchSuggestion } from './company-suggestions-table';
import { Stack } from '@/ui/line/line';
import { Logo } from '@/ui/logo/logo';
import { ParagraphSmall } from '@/ui/typography/widgets';
import { LinkIcon } from '@/ui/link/link-icon';
import { useTranslation } from 'react-i18next';
import { SkeletonText } from '@/ui/table/infinite-table/skeleton-row';
import { formatLocation } from '@/helpers/format-location';
import {
  LightTooltip,
  SuggestionWrapper,
  TextValue,
  TooltipTitle,
} from '../enriched-table/ui';
import { Tooltip } from '@/ui/tooltip/tooltip';

// Color variables
const COLORS = {
  websiteActive: '#007AFF',
  linkedInActive: '#0A66C2',
  inactive: '#D6DDF8',
};

type CompanyRowMainCellProps = Pick<
  CompanySearchSuggestion['enrichedData'],
  'keyOffering' | 'location' | 'website' | 'linkedInUrl'
> &
  Pick<CompanySearchSuggestion['basicData'], 'name' | 'logoUrl'> & {
    isEnrichDataLoading: boolean;
  };

export const CompanyRowMainCell = ({
  name,
  logoUrl,
  keyOffering,
  location,
  website,
  linkedInUrl,
  isEnrichDataLoading,
}: CompanyRowMainCellProps) => {
  const { t } = useTranslation('default', {
    keyPrefix: 'globalSearch.suggestionsTable',
  });

  return (
    <SuggestionWrapper
      name="company-suggestion-main-cell"
      justify="space-between"
      alignContent="center"
      alignItems="center"
    >
      <Flexbox name="company-suggestion-data">
        <Logo
          border="none"
          name={name}
          logoUrl={logoUrl}
          size="32px"
          singleLetter
          fontSize="14px"
        />
        <Stack gap="0" margin="0 10px 0 12px" width="180px">
          <LightTooltip
            content={
              <Stack gap="3px">
                <TooltipTitle>{t`keyOffering`}</TooltipTitle>
                {keyOffering}
              </Stack>
            }
            visible={!!keyOffering}
            withArrow
            padding="14px 20px"
          >
            <ParagraphSmall>{name}</ParagraphSmall>
          </LightTooltip>
          {isEnrichDataLoading ? (
            <SkeletonText width="80%" />
          ) : (
            <TextValue>
              {location
                ? formatLocation(
                    {
                      cityName: location.city?.name,
                      countryName: location.country?.name,
                      regionName: location.region?.name,
                    },
                    {
                      compact: true,
                    },
                  )
                : ''}
            </TextValue>
          )}
        </Stack>
        <Flexbox gap="8px" flex="auto" alignItems="center">
          <Tooltip content={website} visible={!!website}>
            <LinkIcon
              disableTooltip
              url={website ?? ''}
              icon="Website"
              color={website ? COLORS.websiteActive : COLORS.inactive}
              stopPropagation
            />
          </Tooltip>
          <Tooltip content={linkedInUrl} visible={!!linkedInUrl}>
            <LinkIcon
              disableTooltip
              url={linkedInUrl ?? ''}
              icon="LinkedIn"
              color={linkedInUrl ? COLORS.linkedInActive : COLORS.inactive}
              stopPropagation
            />
          </Tooltip>
        </Flexbox>
      </Flexbox>
    </SuggestionWrapper>
  );
};
